import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { htmlToText } from "../../../utils/toDisplayString";
import { faServer } from "@fortawesome/free-solid-svg-icons";
import { formatDate } from "./examUtils";
import RecentPurchasesPanel from "./RecentPurchasesPanel";
import { Skeleton } from "../common/AdminSkeleton";

// Renders the /api/admin/exam-dashboard payload: alerts, inventory, purchases,
// fulfillment, emails, providers and recent activity.
const ExamDashboardView = ({ dashboard, loading, hideRecentPurchases = false, showRecent = true }) => {
  if (loading && !dashboard) {
    return (
      <div className="space-y-3" aria-busy="true" aria-label="Loading exam dashboard">
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <Skeleton className="h-4 w-40" />
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="rounded-lg border border-slate-100 bg-slate-50 p-3">
                <Skeleton className="h-3 w-2/3" />
                <Skeleton className="mt-2 h-5 w-1/2" />
              </div>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
              <Skeleton className="h-3 w-1/3" />
              <div className="mt-3 space-y-2">
                {Array.from({ length: 3 }).map((__, row) => (
                  <Skeleton key={row} className="h-8 w-full" />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 bg-white p-4 text-center text-sm text-slate-500">
        Dashboard metrics are unavailable right now.
      </div>
    );
  }

  // Compact stat chip — the label (header name) always stays visible above the value.
  const statChip = (label, value, valueClass = "text-slate-900") => (
    <div className="rounded-md border border-slate-100 bg-slate-50 px-2 py-1.5">
      <p className="m-0 text-[10px] font-semibold uppercase leading-tight tracking-wide text-slate-500">
        {label}
      </p>
      <p className={`m-0 text-base font-bold leading-tight ${valueClass}`}>{value ?? 0}</p>
    </div>
  );

  return (
    <div className="space-y-3">
      {/* Background revalidation — cached data stays visible while we refetch */}
      {loading && (
        <div className="flex items-center gap-2 text-[11px] text-slate-400">
          <span className="h-3 w-3 animate-spin rounded-full border-2 border-slate-200 border-t-slate-500" />
          Refreshing...
        </div>
      )}

      {/* INVENTORY */}
      <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
        <h4 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-600">
          Inventory
        </h4>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          {statChip("Total", dashboard.inventory?.total)}
          {statChip("Available", dashboard.inventory?.available)}
          {statChip("Allocated", dashboard.inventory?.allocated)}
          {statChip(
            "Low Stock",
            dashboard.inventory?.lowStock ? "YES" : "No",
            dashboard.inventory?.lowStock ? "text-red-600" : "text-emerald-600"
          )}
        </div>
      </div>

      {/* PURCHASES */}
      <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
        <h4 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-600">
          Purchases
        </h4>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
          {statChip("Total", dashboard.purchases?.total)}
          {statChip("Successful", dashboard.purchases?.successful, "text-emerald-600")}
          {statChip("Pending", dashboard.purchases?.pending, "text-amber-600")}
          {statChip("Failed", dashboard.purchases?.failed, "text-red-600")}
          {statChip("Today", dashboard.purchases?.today)}
          {statChip("Today Successful", dashboard.purchases?.todaySuccessful, "text-emerald-600")}
        </div>
      </div>

      {/* FULFILLMENT */}
      <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
        <h4 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-600">
          Fulfillment
        </h4>
        <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
          {statChip("Fulfilled", dashboard.fulfillment?.fulfilled, "text-emerald-600")}
          {statChip("Pending Fulfillment", dashboard.fulfillment?.pendingFulfillment, "text-amber-600")}
          {statChip("Failed Fulfillment", dashboard.fulfillment?.failedFulfillment, "text-red-600")}
          {statChip("Inventory Exhausted", dashboard.fulfillment?.inventoryExhausted)}
        </div>
      </div>

      {/* EMAIL DELIVERY */}
      <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
        <h4 className="mb-2 text-[11px] font-bold uppercase tracking-wide text-slate-600">
          Email Delivery
        </h4>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
          {statChip("Total", dashboard.emails?.total)}
          {statChip("Sent", dashboard.emails?.sent, "text-emerald-600")}
          {statChip("Pending", dashboard.emails?.pending, "text-amber-600")}
          {statChip("Retrying", dashboard.emails?.retrying, "text-sky-600")}
          {statChip("Failed", dashboard.emails?.failed, "text-red-600")}
        </div>
      </div>

      {/* EMAIL PROVIDERS */}
      {dashboard.providers && (
        <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
          <h4 className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-slate-600">
            <FontAwesomeIcon icon={faServer} className="text-slate-400" />
            Email Providers
          </h4>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
            {[
              { key: "resend", label: "Resend" },
              { key: "mailersend", label: "MailerSend" },
              { key: "brevo", label: "Brevo" },
            ].map((p) => {
              const prov = dashboard.providers[p.key];
              if (!prov) return null;
              return (
                <div
                  key={p.key}
                  className="rounded-md border border-slate-100 bg-slate-50 px-2.5 py-2"
                >
                  <p className="mb-1 text-[10px] font-bold uppercase tracking-wide text-slate-600">
                    {p.label}
                  </p>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-slate-500">Attempts</span>
                    <span className="font-semibold text-slate-800">{prov.attempts ?? 0}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-emerald-600">Succeeded</span>
                    <span className="font-semibold text-emerald-700">{prov.succeeded ?? 0}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-red-600">Failed</span>
                    <span className="font-semibold text-red-700">{prov.failed ?? 0}</span>
                  </div>
                </div>
              );
            })}
            <div className="rounded-md border border-slate-100 bg-slate-50 px-2.5 py-2">
              <p className="mb-1 text-[10px] font-bold uppercase tracking-wide text-slate-600">
                Fallback
              </p>
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-500">Attempts</span>
                <span className="font-semibold text-slate-800">
                  {dashboard.providers.fallbackAttempts ?? 0}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recent activity — rendered by ExamRecentActivity so a page can place the
          lists elsewhere (the Exam Purchases page shows them after the table). */}
      {showRecent && (
        <ExamRecentActivity dashboard={dashboard} hideRecentPurchases={hideRecentPurchases} />
      )}
    </div>
  );
};

/**
 * The recent-activity lists — Recent Purchases, Failed Emails and Fulfillment
 * Events. Every panel spans the full width and scrolls its own list, and the
 * block is exported so it can be rendered independently of the stats above.
 */
export const ExamRecentActivity = ({ dashboard, hideRecentPurchases = false }) => {
  const recent = dashboard?.recent;
  if (!recent) return null;

  return (
    <div className="flex w-full flex-wrap gap-3">
      {!hideRecentPurchases && <RecentPurchasesPanel purchases={recent.purchases} />}

      {recent.failedEmails?.length > 0 && (
        <RecentPanel title="Failed Emails" accent="text-red-600">
          {recent.failedEmails.map((e) => (
            <div key={e.id} className="border-b border-slate-100 py-2 last:border-0">
              <p className="m-0 truncate text-sm font-semibold text-slate-800">{htmlToText(e.recipient)}</p>
              <p className="m-0 truncate text-xs text-slate-500">
                {htmlToText(e.reference)} {e.provider && `• ${htmlToText(e.provider)}`} • {e.attempts} attempts
              </p>
              {e.lastError && <p className="m-0 truncate text-[11px] text-red-500">{htmlToText(e.lastError)}</p>}
              <p className="m-0 text-[11px] text-slate-400">{formatDate(e.failedAt)}</p>
            </div>
          ))}
        </RecentPanel>
      )}

      {recent.fulfillmentEvents?.length > 0 && (
        <RecentPanel title="Fulfillment Events" accent="text-sky-600">
          {recent.fulfillmentEvents.map((ev) => (
            <div
              key={ev.id}
              className="flex items-center justify-between gap-2 border-b border-slate-100 py-2 last:border-0"
            >
              <div className="min-w-0">
                <p className="mb-0.5 truncate text-sm font-semibold text-slate-800">{htmlToText(ev.action)}</p>
                <span className="badge text-bg-secondary">{htmlToText(ev.actorType)}</span>
              </div>
              <span className="shrink-0 text-[11px] text-slate-400">
                {formatDate(ev.createdAt)}
              </span>
            </div>
          ))}
        </RecentPanel>
      )}
    </div>
  );
};

/**
 * Full-width panel with a fixed-height, scrollable body — the header stays put
 * while the list scrolls (same pattern as RecentPurchasesPanel).
 */
const RecentPanel = ({ title, accent, className = "", children }) => (
  <div
    className={`flex max-h-[500px] min-h-[120px] min-w-0 flex-1 basis-[320px] flex-col rounded-lg border border-slate-200 bg-white p-3 shadow-sm ${className}`}
  >
    <h4 className={`mb-2 shrink-0 text-[11px] font-bold uppercase tracking-wide ${accent}`}>
      {title}
    </h4>
    <div className="min-h-0 flex-1 overflow-y-auto pr-1">{children}</div>
  </div>
);

export default ExamDashboardView;
